import { css } from "@emotion/react";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { UploaderProps, UploaderRef } from "./types";
import { styles } from "./styles";
import UploaderFileLists from "./FileLists";
import EmptyContent from "./EmptyContent";
import FormField from "../FormField";
import { useFileUploader } from "./useFileUploader";

/**
 * `Uploader` allows users to upload files via the local file system or by dragging and dropping files into the component.
 */
const Uploader = forwardRef(function <ProcessedFile = File>(
  {
    multiple = false,
    onChange,
    accepts,
    error = "",
    maxFileSize = Number.MAX_VALUE,
    disabled = false,
    skipFileRead = false,
    onlyFileList = false,
    processFile,
    listVariant = "default"
  }: UploaderProps<ProcessedFile>,
  ref: React.ForwardedRef<UploaderRef>
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    isEmpty,
    isDragging,
    uploadedFiles,
    successFiles,
    fileErrors,
    successfullyProcessedFiles,
    processingFiles,
    onDelete,
    onFileDrop,
    openFilePicker,
    handleInputChange,
    onReselect,
    clearFiles,
    setIsDragging
  } = useFileUploader<ProcessedFile>({
    multiple,
    onChange,
    accepts,
    maxFileSize,
    processFile,
    skipFileRead,
    inputRef
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const hadFilesRef = useRef(false);

  useEffect(() => {
    if (
      !uploadedFiles.size &&
      !processingFiles.size &&
      !fileErrors.size &&
      !successfullyProcessedFiles.size
    ) {
      return;
    }
    containerRef.current
      ?.querySelector<HTMLButtonElement>("button:last-of-type")
      ?.focus();
  }, [uploadedFiles, processingFiles, fileErrors, successfullyProcessedFiles]);

  // After the last file clears, return focus to the dropzone so keyboard users
  // keep their place. Guarded by hadFilesRef to avoid focusing on mount.
  useEffect(() => {
    const isEmptyNow =
      !uploadedFiles.size &&
      !processingFiles.size &&
      !fileErrors.size &&
      !successfullyProcessedFiles.size;
    if (isEmptyNow && hadFilesRef.current) {
      containerRef.current?.focus();
    }
    hadFilesRef.current = !isEmptyNow;
  }, [uploadedFiles, processingFiles, fileErrors, successfullyProcessedFiles]);

  useImperativeHandle(ref, () => {
    return {
      get element() {
        return inputRef.current;
      },
      clearFiles
    };
  }, [clearFiles]);

  const uploaderContent = (
    <div
      className="kit-Uploader"
      ref={containerRef}
      css={[
        styles.container,
        !isEmpty && styles.containerListVariant[listVariant],
        isDragging && styles.dragging,
        !!error && styles.containerError,
        disabled && styles.disabled,
        isEmpty && onlyFileList && styles.isEmptyAndOnlyFileList
      ]}
      onClick={({ currentTarget }) => {
        currentTarget.blur();
        if (!disabled) {
          openFilePicker();
        }
      }}
      onKeyDown={event => {
        if (!disabled && (event.key === "Enter" || event.key === " ")) {
          openFilePicker();
        }
      }}
      onDragLeave={() => setIsDragging(false)}
      onDragOver={event => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDrop={event => !disabled && onFileDrop(event)}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="file uploader"
      aria-disabled={disabled}
    >
      <div css={styles.contentWrapper}>
        {isEmpty && !onlyFileList && (
          <EmptyContent
            accepts={accepts}
            multiple={multiple}
            disabled={disabled}
          />
        )}
        <UploaderFileLists
          isEmpty={isEmpty}
          fileErrors={fileErrors}
          processingFiles={processingFiles}
          successFiles={successFiles}
          onDelete={onDelete}
          onReselect={onReselect}
          listVariant={listVariant}
        />
        {!!error && isEmpty ? (
          <span css={styles.uploaderError}>
            {typeof error === "string" ? error : undefined}
          </span>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accepts.join(",")}
          css={css`
            display: none;
          `}
          onChange={handleInputChange}
          disabled={disabled}
        />
      </div>
    </div>
  );

  return (
    <FormField error={!isEmpty ? error : undefined} fill>
      {disabled ? (
        <div css={styles.disabledWrapper}>{uploaderContent}</div>
      ) : (
        uploaderContent
      )}
    </FormField>
  );
}) as <ProcessedFile = File>(
  props: UploaderProps<ProcessedFile> & {
    ref?: React.ForwardedRef<UploaderRef>;
  }
) => React.ReactElement;

export default Uploader;
export * from "./types";
